/**
 * Live-trading one-click arm control (live-tennis V1b).
 *
 * The single visible truth for whether price clicks place orders. Disarmed
 * (always the state on load) the dashboard behaves exactly as before —
 * price clicks open the ticket panel. Armed, Kalshi price clicks submit
 * immediately at the displayed size, so the control:
 *
 *   • wears an unmissable armed treatment (filled red, pulsing dot);
 *   • keeps the size input beside the toggle — the packet's rule that the
 *     effective size is visible at the interaction surface;
 *   • doubles as the kill switch: one click disarms instantly, and the ack
 *     line only ever reports what actually happened (pending → accepted /
 *     rejected / throttled), never a presumed success.
 *
 * Kalshi-only in V1 — Polymarket clicks keep the panel flow even when armed.
 */

import { useEffect } from 'react';
import { Zap } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import {
  useOneClickStore,
  ONE_CLICK_FALLBACK_MAX_CONTRACTS,
  ONE_CLICK_MAX_CONTRACTS,
  type OneClickAck,
} from '@/stores/oneClickStore';
import { useOneClickAccess } from '@/hooks/useOneClickAccess';
import { useAuthStore } from '@/stores/authStore';

function ackTone(status: OneClickAck['status']): string {
  switch (status) {
    case 'accepted':
      return 'text-positive';
    case 'rejected':
      return 'text-negative';
    case 'throttled':
    case 'unverified':
      return 'text-amber-400';
    default:
      return 'text-muted-foreground';
  }
}

function ackText(ack: OneClickAck): string {
  switch (ack.status) {
    case 'pending':
      return `Placing ${ack.label}…`;
    case 'accepted':
      return `Placed ${ack.label}`;
    case 'rejected':
      return `Rejected ${ack.label}${ack.message ? ` — ${ack.message}` : ''}`;
    case 'throttled':
      return ack.message ?? 'Click ignored — pacing';
    case 'unverified':
      return `${ack.label} — ${ack.message ?? 'accepted but not visible; check receipts'}`;
  }
}

export function OneClickArmControl() {
  const oneClickAccess = useOneClickAccess();
  // Per-trader server ceiling (traderRules.oneClickMaxContracts, D-020
  // LT2-010). Presentation clamp only — the relay enforces regardless.
  const serverCeiling = useAuthStore((s) => s.user?.oneClickMaxContracts ?? null);
  const effectiveMax =
    serverCeiling != null
      ? Math.min(ONE_CLICK_MAX_CONTRACTS, serverCeiling)
      : ONE_CLICK_FALLBACK_MAX_CONTRACTS;
  const armed = useOneClickStore((s) => s.armed);
  const sizeContracts = useOneClickStore((s) => s.sizeContracts);
  const lastAck = useOneClickStore((s) => s.lastAck);
  const arm = useOneClickStore((s) => s.arm);
  const disarm = useOneClickStore((s) => s.disarm);
  const setSizeContracts = useOneClickStore((s) => s.setSizeContracts);

  // Revocation hides the control and synchronously converges the local mode
  // back to safe on the next effect. The relay gate remains authoritative for
  // any request already in flight.
  useEffect(() => {
    if (!oneClickAccess) disarm();
  }, [oneClickAccess, disarm]);

  // A persisted size may have been chosen by a trader with a larger explicit
  // ceiling. When the active identity changes, converge the visible setting
  // to the new trader's authority instead of displaying an unusable value.
  useEffect(() => {
    if (sizeContracts > effectiveMax) setSizeContracts(effectiveMax);
  }, [effectiveMax, setSizeContracts, sizeContracts]);

  if (!oneClickAccess) return null;

  return (
    <div className="flex min-w-0 items-center gap-2">
      <button
        type="button"
        data-lt2-hotkey-control
        onClick={armed ? disarm : arm}
        title={
          armed
            ? 'One-click trading ARMED — Kalshi price clicks place orders immediately. Click to disarm.'
            : 'Arm one-click trading: Kalshi price clicks place orders immediately at the size shown (session-only; disarms on refresh).'
        }
        className={cn(
          'flex shrink-0 items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-semibold transition-colors',
          armed
            ? 'border-negative bg-negative/90 text-white'
            : 'border-border/50 bg-muted/50 text-muted-foreground hover:text-foreground',
        )}
      >
        {armed && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" aria-hidden />}
        <Zap size={13} aria-hidden />
        {armed ? '1-CLICK ARMED' : '1-Click'}
      </button>
      <label
        className={cn(
          'flex shrink-0 items-center gap-1 font-mono text-xs',
          armed ? 'text-foreground' : 'text-muted-foreground',
        )}
        title={`One-click order size (contracts, max ${effectiveMax})`}
      >
        <span>×</span>
        <input
          type="number"
          min={1}
          max={effectiveMax}
          value={sizeContracts}
          onChange={(e) =>
            setSizeContracts(Math.min(effectiveMax, parseInt(e.target.value, 10)))
          }
          className="border-border/50 bg-background w-16 rounded border px-1.5 py-0.5 text-right"
        />
      </label>
      {armed && lastAck && (
        <span
          role="status"
          // Bounded status region (Phase 3 Loom hard failure): horizontal
          // growth is CAPPED and the full reason WRAPS inside it — a long
          // rejection must never stretch the toolbar, compress the sibling
          // controls, or hide its tail behind a hover. The title merely
          // duplicates the visible text for copy/hover convenience.
          className={cn(
            'min-w-0 max-w-[300px] whitespace-normal break-words font-mono text-[11px] leading-tight',
            ackTone(lastAck.status),
          )}
          title={ackText(lastAck)}
        >
          {ackText(lastAck)}
        </span>
      )}
    </div>
  );
}
